import {AfterContentInit, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {BasketsFacade} from '@kion/data-access/data-access-baskets';
import {BehaviorSubject, combineLatest, interval, Observable, Subject} from 'rxjs';
import {Basket, Payment, PaymentInfo} from '@kion/data-models';
import {debounce, map, take} from 'rxjs/operators';
import {CmsConfig} from '../../config/cms.config';
import {CorrelationObserverService} from '@kion/ngrx-helper';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {ToastService} from '@kion/ui';
import {Router} from '@angular/router';
import {SubSink} from 'subsink';
import {TranslateService} from '@ngx-translate/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

declare const PayEngineWidget: any;

interface PaymentConfig {
  language: string,
  successUrl: string,
  failureUrl: string,
  customerStyleId: string,
  merchantId: string,
  code: string
}


@Component({
  selector: 'kion-cms-checkout-payment',
  templateUrl: './checkout-payment.component.html',
  styleUrls: ['./checkout-payment.component.scss']
})
export class CheckoutPaymentComponent implements OnInit, AfterContentInit, OnDestroy {

  payments$: Observable<Payment[]>;
  paymentConfig$: Subject<PaymentConfig[]> = new Subject<PaymentConfig[]>();
  language: string;

  selectedPaymentMethod$: BehaviorSubject<Payment> = new BehaviorSubject<Payment>(null);
  basket$: Observable<Basket>;
  canProceed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  backLink: string;

  subSink: SubSink;

  form: FormGroup;

  constructor(
    private basketsFacade: BasketsFacade,
    private cmsConfig: CmsConfig,
    private ngZone: NgZone,
    private observer: CorrelationObserverService,
    private toastService: ToastService,
    private router: Router,
    private translateService: TranslateService,
    formBuilder: FormBuilder
  ) {
    this.language = 'de';
    if (this.cmsConfig.isoCode === 'gb') {
      this.language = 'en';
    }
    if (this.cmsConfig.isoCode === 'hu') {
      this.language = 'hu';
    }
    if (this.cmsConfig.isoCode === 'cz') {
      this.language = 'en';
    }
    if (this.cmsConfig.isoCode === 'se') {
      this.language = 'sv';
    }
    if (this.cmsConfig.isoCode === 'sk') {
      this.language = 'sk';
    }
    const baseRoute = this.cmsConfig.routes?.checkoutBaseRoute;
    this.backLink = '/' + baseRoute + '/' + this.cmsConfig.routes?.checkoutRoutes?.delivery;
    this.subSink = new SubSink();
    this.form = formBuilder.group({
      poField: new FormControl(null, [Validators.required, Validators.maxLength(50)])
    })
  }

  get poField(): AbstractControl {
    return this.form.get('poField');
  }

  ngOnInit(): void {
    this.basketsFacade.createOrFindBasket();
    this.payments$ = this.basketsFacade.payments$;
    this.basket$ = this.basketsFacade.all$.pipe(
      map(
        (baskets: Basket[]) => baskets.find(
          (basket: Basket) => basket.id === window.localStorage.getItem('basketId')
        )
      )
    );
    const body = <HTMLDivElement>document.body;
    const widget = document.createElement('script');
    widget.innerHTML = '';
    widget.src = this.cmsConfig.prodPayment ? './assets/payment/prod/widget.min.js' : './assets/payment/widget.min.js';
    widget.async = true;
    widget.defer = true;
    body.appendChild(widget);
    const bridge = document.createElement('script');
    bridge.innerHTML = '';
    bridge.src = this.cmsConfig.prodPayment ? './assets/payment/prod/bridge.min.js' : './assets/payment/bridge.min.js';
    bridge.async = true;
    bridge.defer = true;
    body.appendChild(bridge);
    this.subSink.sink = this.basket$.subscribe(
      (basket: Basket) => {
        const poField = this.form.get('poField');
        if (basket && basket.purchaseOrderNumber && !poField.value) {
          poField.setValue(basket.purchaseOrderNumber)
        }
      }
    );
    this.form.get('poField').valueChanges
      .pipe(debounce(() => interval(1000)))
      .subscribe(
        value => {
          this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
            (basket: Basket) => {
              if (value !== basket.purchaseOrderNumber) {
                this.basketsFacade.updatePurchaseOrderNumber(basket.id, value)
              }
            }
          )
        }
      )
  }

  ngAfterContentInit(): void {
    this.subSink.sink = this.observer.subscribeToAction(
      this.basketsFacade.loadPayments(),
      () => setTimeout(() => this.loadPayments(), 2000),
      () => {
        console.log("error")
      }
    );
    this.subSink.sink = combineLatest(
      [
        this.basket$,
        this.payments$
      ]
    ).subscribe(
      ([basket, payments]) => {
        if (basket && basket.paymentMode) {
          const selectedPayment = payments.find(
            (payment: Payment) => payment.code === basket.paymentMode.code
          );
          this.selectedPaymentMethod$.next(selectedPayment);
          this.canProceed$.next(true);
        }
      }
    )
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  paymentSelected(paymentMethod: Payment) {
    this.selectedPaymentMethod$.next(paymentMethod);
    // do not this when selected payment is credit card
    if (paymentMethod.paymentConfiguration === undefined) {
      this.canProceed$.next(true);
    }
  }

  preAuthorize(): void {
    const payment = this.selectedPaymentMethod$.value;
    const iframe = document.getElementById('payment-widget-' + payment.code);
    if (iframe && iframe.children.length > 0) {
      // @ts-ignore
      window.PayEngineWidget.pay(document.getElementById('payment-widget-' + payment.code).children.item(0).id, () => {
        //NOP
      });
      return;
    }
    const paymentInfo: Partial<PaymentInfo> = {
      paymentConfiguration: {
        paymentModeCode: payment.code
      }
    };
    if (payment.isPurchaseOrderIdNeeded && !this.form.valid) {
      this.form.markAsDirty();
      this.form.markAllAsTouched();
      return;
    }
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        this.observer.subscribeToAction(
          this.basketsFacade.setPayment(
            basket.id,
            paymentInfo
          ),
          () => {
            this.navigateToSuccessStep()
          }
          ,
          () => {
            this.showErrorToast();
          }
        )
      }
    )
  }

  private showErrorToast() {
    this.toastService.show(
      {
        type: 'error',
        text: this.translateService.instant('toast.checkout.payment.save.error')
      }
    )
  }

  private navigateToSuccessStep() {
    this.router.navigate([this.cmsConfig.routes.checkoutBaseRoute, this.cmsConfig.routes.checkoutRoutes.summary]).then(() => {
      //NOP
    });
  }

  private loadPayments(): void {
    this.subSink.sink = this.payments$.pipe(take(1)).subscribe(
      (payments: Payment[]) => {
        if (payments && payments.length > 0) {
          this.paymentConfig$.next(
            payments.map(
              (payment: Payment) => {
                if (payments.length === 1 && payment.paymentConfiguration === undefined) {
                  this.canProceed$.next(true);
                }
                const config = {
                  language: this.language,
                  successUrl: payment.paymentConfiguration?.confirmationUrl,
                  failureUrl: payment.paymentConfiguration?.returnUrl,
                  customerStyleId: payment.paymentConfiguration?.customStyleId,
                  merchantId: payment.paymentConfiguration?.merchantId,
                  code: payment.code
                };
                if (!!payment.paymentConfiguration?.merchantId) {
                  const optionalParameters = {
                    "language": config.language,
                    "layout": "dropdown",
                    "customStyleId": config.customerStyleId,
                    "cancelUrl": config.failureUrl,
                    "returnUrl": config.failureUrl,
                    "hidePayButton": true,
                    "hideTitleIcons": false,
                    "skipFinalResultView": true,
                    "paymentDetails": {
                      "paypal": {
                        "returnUrl": payment.paymentConfiguration?.returnUrl,
                        "cancelUrl": payment.paymentConfiguration?.returnUrl
                      }
                    },
                    "optionalFields": {
                      "creditCard": {
                        "cardHolder": true
                      },
                      "paypal": null
                    },
                    "products": [
                      payment.type === "KionConcardisCreditCardPaymentModeModel" ? 'creditcard' : 'paypal'
                    ]
                  };
                  // @ts-ignore
                  optionalParameters.initCallback = () => {
                    this.canProceed$.next(true);
                  };
                  // @ts-ignore
                  optionalParameters.cofAgreementStateCallback = () => {
                    //NOP
                  };

                  const elementId = 'payment-widget-' + config.code;
                  const element = document.getElementById(elementId);
                  if (element) {
                    element.innerHTML = '';
                  }
                  this.initPayEngineWidgetAsComponent(elementId, config, optionalParameters);
                }
                return config;
              }
            )
          )
        }
      }
    );
  }

  private initPayEngineWidgetAsComponent(elementId: string, config: { code: string; merchantId: string | undefined; customerStyleId: string | undefined; successUrl: string | undefined; language: string; failureUrl: string | undefined }, optionalParameters: { layout: string; cancelUrl: string; hideTitleIcons: boolean; customStyleId: string; skipFinalResultView: boolean; language: string; returnUrl: string; hidePayButton: boolean; paymentDetails: { paypal: { cancelUrl: string | undefined; returnUrl: string | undefined } }; optionalFields: { creditCard: { cardHolder: boolean }; paypal: null }; products: (string)[] }) {
    PayEngineWidget.initAsInlineComponentPi(
      elementId,
      config.merchantId,
      optionalParameters,
      (error, paymentInstrument) => this.ngZone.run(() => {
        if (paymentInstrument && paymentInstrument.origin === 'creditcard') {
          this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
            (basket: Basket) => {
              this.subSink.sink = this.observer.subscribeToAction(
                this.basketsFacade.setPayment(
                  basket.id,
                  {
                    paymentConfiguration: {
                      brand: paymentInstrument.attributes.brand,
                      cardHolder: paymentInstrument.attributes.cardHolder,
                      cardNumber: paymentInstrument.attributes.cardNumber,
                      expiryMonth: paymentInstrument.attributes.expiryMonth,
                      expiryYear: paymentInstrument.attributes.expiryYear,
                      paymentInstrumentId: paymentInstrument.paymentInstrumentId,
                      paymentModeCode: config.code
                    }
                  }
                ),
                () => {
                  this.navigateToSuccessStep()
                },
                () => {
                  this.showErrorToast()
                }
              )
            }
          )
        }
      })
    );
  }

}
